<script lang="ts">
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import StockTextDisplay from "@retailer/svelte/CartWidget/StockTextDisplay.svelte";
  import type { PickupWarehouse } from "@shared/types/PickupWarehouse";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import type { Distributor } from "@shared/types/Distributor";

  export let selectedPickupLocationId: number;
  export let pickupOptions: PickupWarehouse[] = [];
  export let distributor: Partial<Distributor>;
  export let onPickupSelection: (selectedPickupLocation: PickupWarehouse) => void;
  export let onCancel: () => void;
  export let isOnListsOrDealsPage: boolean = false;
  export let shouldDisplayTime: boolean = false;
  export let isOnRepRecommendationsPage: boolean = false;
  export let shouldShowLowStock: boolean = false;

  const outOfStock: string = "Out of stock";

  const isOutOfStock = (stock: string) => stock == outOfStock;

  $: noPickupLocationSelected = selectedPickupLocationId == null;

  const noAccountNumberText = `${distributor?.name} requires an account number to select in-store pickup.`;
</script>

<style>
  .pickup-list {
    max-height: 65vh;
    overflow-y: auto;
  }

  header {
    position: sticky;
  }

  footer {
    position: sticky;
    bottom: 0;
    right: 0;
    width: 100%;
    background: white;
    z-index: 1;
    border-top: 1px solid #eaeaea;
  }
</style>

<Modal show={true} closeOnBackgroundClick={false} fizzModal={true} size={"small"}>
  <section class="relative" data-testid="pickup-location-modal">
    <header class="fizz-inset-square-16 fizz-border-bottom fizz-space-between">
      <h4 class="fizz-heading-4">Choose Store</h4>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div data-testid="close-pickup-location-modal" on:click|preventDefault={onCancel}>
        <Icon type="X" extraClassNames="cursor-pointer fizz-icon-16 fizz-text-subdued" />
      </div>
    </header>
    <div class="pickup-list">
      {#if pickupOptions.length == 0}
        <div
          class="fizz-inset-square-32 fizz-border-bottom fizz-text-left fizz-text-medium"
          data-testid="no-pickup-available"
        >
          <p class="fizz-stack-8">No stores available.</p>
          <p class="fizz-stack-8">{noAccountNumberText}</p>
          <p>
            If you already have an account number, please reach out to your sales rep to update your
            pickup store information.
          </p>
        </div>
      {:else}
        {#each pickupOptions as pickupOption}
          <div
            data-testid="pickup-location-card-{pickupOption.id}"
            class="fizz-border-bottom fizz-inset-square-16 fizz-flex-row fizz-space-between"
            class:fizz-surface-gray={isOutOfStock(pickupOption.stock) &&
              !distributor?.allow_ordering_out_of_stock}
          >
            <label class="fizz-flex-row" for="pickup-location-{pickupOption.id}">
              <input
                type="radio"
                id="pickup-location-{pickupOption.id}"
                class="fizz-radio-button"
                bind:group={selectedPickupLocationId}
                name="pickup-location"
                value={pickupOption.id}
                disabled={isOutOfStock(pickupOption.stock) &&
                  !distributor?.allow_ordering_out_of_stock}
              />
              <div class="fizz-text-left">
                <div class="fizz-text-strong">{pickupOption.name}</div>
                {#if pickupOption.address}
                  <div class="fizz-text-subdued" data-testid="{pickupOption.id}-address">
                    {pickupOption.address}
                  </div>
                {/if}

                <StockTextDisplay
                  {isOnListsOrDealsPage}
                  {shouldDisplayTime}
                  {isOnRepRecommendationsPage}
                  stock={pickupOption.stock}
                  {shouldShowLowStock}
                  shouldShowNoRecentUpdate={false}
                  showVerifiedInStock={false}
                  isOnChooseStoreModal={true}
                />
              </div>
            </label>
          </div>
        {/each}
      {/if}
    </div>
    <footer class="fizz-flex-row fizz-justify-end fizz-inset-square-16">
      <button
        class="fizz-button-secondary fizz-button"
        data-testid="save-pickup-location-selection"
        on:click|preventDefault={() =>
          onPickupSelection(pickupOptions.find(po => po.id === selectedPickupLocationId))}
        disabled={noPickupLocationSelected}
      >
        Save
      </button>
    </footer>
  </section>
</Modal>
